import { assertNever } from '../assert.js';
import { actsAsStaff } from '../authz/routes.js';
import { isTerminal } from '../runs/run.js';
/**
 * Who has to agree before a run reaches someone's infrastructure (IM-102, IM-39).
 *
 * One model for two stories. IM-102 is a customer approving what Qrvey runs in
 * their account; IM-39 is a second person approving the destroy of an instance
 * Qrvey cannot afford to lose. Both are "record the request, dispatch nothing,
 * wait for somebody who is not the requester" — the only difference is *who*
 * that somebody may be, which is `ApprovalRequirement`.
 *
 * Everything here is pure. The handler reads the facts (the tenant's kind, the
 * customer's settings, the protection mark) and this decides what they mean, so
 * the console can ask the same question before a run is submitted and get the
 * same answer the API will give.
 */
/**
 * How long a request waits before it expires.
 *
 * Seven days: long enough to cover a customer's change-approval meeting that
 * happens weekly, short enough that an approval cannot be given to a plan whose
 * world has moved on underneath it — the chart version, the variables and the
 * instance's state were all read when the run was requested. An expired request
 * is a recorded terminal state, and asking again costs one click.
 *
 * It also sets how long a pending run's secret variables are kept: see
 * `RunSecretStore`, whose TTL for a pending run is this window plus a day.
 */
export const APPROVAL_WINDOW_DAYS = 7;
const DAY_MS = 86_400_000;
/** When a request made at `requestedAt` stops being approvable. */
export const approvalExpiry = (requestedAt) => new Date(new Date(requestedAt).getTime() + APPROVAL_WINDOW_DAYS * DAY_MS).toISOString();
/**
 * Whether a command could ever be auto-approved. A total switch, so a fifth
 * command has to say.
 */
export const isAutoApprovable = (command) => {
    switch (command) {
        case 'plan':
        case 'output':
        case 'apply':
            return true;
        case 'destroy':
            return false;
        default:
            return assertNever(command, 'run command');
    }
};
export const AUTO_APPROVABLE_COMMANDS = [
    'plan',
    'apply',
    'output',
];
/**
 * What a customer who has chosen nothing is asked about: everything but `plan`
 * and `output`.
 *
 * Those two change nothing — a plan reports what would change and an output
 * reads the endpoints — so asking about them would train a customer to click
 * Approve without reading, which is how an approval stops meaning anything. An
 * `apply` changes their infrastructure and is asked about by default.
 */
export const DEFAULT_AUTO_APPROVE = ['plan', 'output'];
export class InvalidApprovalSettingsError extends Error {
}
/** The sentence a request to auto-approve `destroy` is refused with. */
export const DESTROY_NEVER_AUTO_APPROVED = 'destroy can never be auto-approved: tearing down an instance cannot be undone, so every destroy Qrvey starts in your account waits for your explicit approval.';
/**
 * Reads the commands a settings request asks to auto-approve, or refuses it.
 *
 * Shared so the console can refuse with the same words, though it never offers
 * `destroy` at all.
 */
export const readAutoApproveRequest = (body) => {
    const value = typeof body === 'object' && body !== null && !Array.isArray(body)
        ? body.autoApprove
        : undefined;
    if (!Array.isArray(value)) {
        throw new InvalidApprovalSettingsError('autoApprove must be a list of commands');
    }
    const chosen = [];
    for (const item of value) {
        if (item === 'destroy')
            throw new InvalidApprovalSettingsError(DESTROY_NEVER_AUTO_APPROVED);
        const known = AUTO_APPROVABLE_COMMANDS.find((command) => command === item);
        if (known === undefined) {
            throw new InvalidApprovalSettingsError(`autoApprove may hold only ${AUTO_APPROVABLE_COMMANDS.join(', ')}; got ${JSON.stringify(item)}`);
        }
        if (!chosen.includes(known))
            chosen.push(known);
    }
    // In the order the commands are listed, so two requests meaning the same
    // thing store the same thing, and an audit before/after is not a reordering.
    return AUTO_APPROVABLE_COMMANDS.filter((command) => chosen.includes(command));
};
/** The auto-approve set in effect: the customer's choice, or the default. */
export const autoApproveIn = (settings) => settings?.autoApprove ?? DEFAULT_AUTO_APPROVE;
/** A setting as a sentence, for the audit trail and an auto-approved run's record. */
export const describeAutoApprove = (settings) => {
    const commands = autoApproveIn(settings);
    const list = commands.length === 0 ? 'nothing' : commands.join(', ');
    return settings === undefined
        ? `auto-approve ${list} (the default; the customer has chosen nothing)`
        : `auto-approve ${list} (set by ${settings.by} at ${settings.at})`;
};
/**
 * Whether a destroy requested by `requester` counts as against a protected
 * instance.
 *
 * Protected while the mark stands — and **also** for the person who removed it,
 * for one approval window afterwards. Removing protection needs
 * `deployment:approve`, which keeps it from `internal-ops`; but `platform-admin`
 * holds approve *and* destroy, so without this they could lift the mark and
 * destroy alone a minute later, which is the one thing four eyes exists to stop.
 * Anybody else sees the removal as a removal.
 */
export const isProtectedFor = (mark, requester, now) => {
    if (mark === undefined)
        return false;
    if (mark.removedAt === undefined)
        return true;
    const sameHands = (mark.removedBy ?? '').trim().toLowerCase() === requester.trim().toLowerCase();
    return (sameHands && now.getTime() < new Date(mark.removedAt).getTime() + APPROVAL_WINDOW_DAYS * DAY_MS);
};
/** Whether a mark is standing, which is what the screens show as "Protected". */
export const isProtected = (mark) => mark !== undefined && mark.removedAt === undefined;
/**
 * Whether anybody at the customer could approve a run right now (IM-176).
 *
 * - `available`: sign-in is on here, and the tenant has an active assignment
 *   in a customer role that holds `deployment:approve`.
 * - `none-assigned`: sign-in is on, but nobody at the customer holds such a
 *   role — no admin has been invited yet, or every one has been revoked.
 * - `sign-in-off`: customer sign-in is not on in this environment (IM-154), so
 *   even a recorded admin cannot reach the page that approves.
 *
 * Three values rather than a boolean because the reason is recorded, and
 * "invite an admin" and "customer sign-in is not live yet" send somebody to
 * different places.
 */
export const CUSTOMER_APPROVER_STATES = ['available', 'none-assigned', 'sign-in-off'];
export const isCustomerApprover = (value) => CUSTOMER_APPROVER_STATES.includes(value);
/** Why a run that would have waited went ahead, as the trail and the run record say it. */
export const NO_APPROVER_REASONS = {
    'none-assigned': 'Went ahead without approval: this customer has nobody who can approve yet (no active admin). Approval will be asked for once they have one.',
    'sign-in-off': 'Went ahead without approval: customer sign-in is not on in this environment, so nobody at the customer can approve yet.',
};
/**
 * Whether this run needs somebody's agreement, and whose.
 *
 * The rules, in the order they are applied:
 *
 * - **In a customer's tenant, the customer's own run needs nobody.** It is their
 *   infrastructure and their decision, and a customer-initiated destroy has
 *   already been through the confirmation dialog. (An assumption to confirm:
 *   "a destroy always requires approval" is read as a *staff* destroy.)
 * - **A destroy started by staff always waits for the customer**, whatever the
 *   settings say — the settings cannot hold one.
 * - **Anything else started by staff waits unless the customer auto-approves
 *   it**, and an auto-approval is recorded with the setting that allowed it.
 * - **Nobody to ask is not a reason to wait** (IM-176). A customer's approval
 *   is required only when an approver exists; otherwise the run goes ahead as
 *   it would with no rule at all — destroy included, because "destroy is never
 *   auto-approved" is about what the customer's *settings* may say, not about
 *   blocking every deployment for a customer who has nobody to say it.
 * - **In Qrvey's own tenants nothing waits**, except the destroy of a protected
 *   instance, which needs a second person.
 */
export const approvalGate = (facts) => {
    switch (facts.policy) {
        case 'customer': {
            if (facts.requestedByCustomer)
                return { outcome: 'none' };
            if (isAutoApprovable(facts.command) &&
                autoApproveIn(facts.settings).includes(facts.command)) {
                return { outcome: 'auto-approved', setting: describeAutoApprove(facts.settings) };
            }
            // After the auto-approval, so a customer's own setting is still what the
            // record names when it applies: that is the stronger answer.
            if (facts.customerApprover !== 'available') {
                return { outcome: 'no-approver', why: NO_APPROVER_REASONS[facts.customerApprover] };
            }
            return {
                outcome: 'pending',
                requirement: 'customer',
                why: isAutoApprovable(facts.command)
                    ? `This customer approves each ${facts.command} Qrvey starts in their account.`
                    : 'A destroy in a customer’s account always waits for the customer’s approval.',
            };
        }
        case 'protected-destroys':
            return facts.command === 'destroy' && facts.protectedInstance
                ? {
                    outcome: 'pending',
                    requirement: 'second-approver',
                    why: 'This instance is protected, so destroying it needs a second person to approve.',
                }
                : { outcome: 'none' };
        default:
            return assertNever(facts.policy, 'run approval policy');
    }
};
/**
 * A pending run whose window has passed, as it should now read.
 *
 * Pure, and idempotent: a run that is not waiting, or not yet due, comes back
 * unchanged. Every read that shows a run applies it, so an expired request
 * never lingers on a screen as "awaiting approval" — and the approval routes
 * also write it, once, conditionally. See `## Approvals` in CLAUDE.md for why
 * nothing sweeps.
 */
export const settleExpiry = (run, now) => {
    if (!isExpiredRequest(run, now))
        return run;
    const at = run.approval?.expiresAt ?? now.toISOString();
    return {
        ...run,
        state: 'expired',
        finishedAt: at,
        ...(run.approval === undefined
            ? {}
            : { approval: { ...run.approval, decision: { outcome: 'expired', at, by: 'system' } } }),
    };
};
/** Waiting, and past its window. */
export const isExpiredRequest = (run, now) => run.state === 'awaiting-approval' &&
    run.approval?.expiresAt !== undefined &&
    now.getTime() >= new Date(run.approval.expiresAt).getTime();
/** Waiting, and still approvable. */
export const isAwaitingApproval = (run, now) => run.state === 'awaiting-approval' && !isExpiredRequest(run, now);
/**
 * Whether this actor may approve or reject this run, or why not.
 *
 * The capability — `deployment:approve` — has already been checked by the
 * route; this is the part a capability cannot express, because it depends on
 * who asked and whose tenant it is:
 *
 * - **No self-approval**, for anyone, platform-admin included. Rejecting your
 *   own request is allowed: it is a withdrawal, and can only mean less happens.
 * - **A customer's approval is the customer's.** Staff hold `deployment:approve`
 *   so they can be the second pair of eyes on Qrvey's own instances; letting
 *   that stand in for a customer's consent would make the consent Qrvey's.
 * - **A break-glass account is never the second approver.** Two break-glass
 *   accounts exist and one person can know both passwords, so four eyes would
 *   become two. Break-glass exists for when Identity Center is down; a protected
 *   destroy can wait for it to come back.
 */
export const decisionRefusal = (run, decider, decision, now) => {
    if (isExpiredRequest(run, now)) {
        return {
            status: 409,
            error: 'approval-expired',
            detail: `This request expired at ${run.approval?.expiresAt ?? 'the end of its window'} without a decision. Start the run again to ask afresh.`,
        };
    }
    if (run.state !== 'awaiting-approval' || run.approval === undefined) {
        return {
            status: 409,
            error: 'not-awaiting-approval',
            detail: isTerminal(run.state)
                ? `This run is not waiting for approval: it is ${run.state}.`
                : `This run is not waiting for approval: it has already been let through and is ${run.state}.`,
        };
    }
    const ownRequest = decider.email.trim().toLowerCase() === run.requestedBy.trim().toLowerCase();
    if (decision === 'approve' && ownRequest) {
        return {
            status: 403,
            error: 'self-approval',
            detail: 'You requested this run, so you cannot also approve it: the point of an approval is that a second person agrees.',
        };
    }
    switch (run.approval.requirement) {
        case 'customer':
            if (actsAsStaff(decider)) {
                return {
                    status: 403,
                    error: 'customer-approval-required',
                    detail: 'Only the customer can approve or reject a run in their own account. Qrvey staff cannot decide on their behalf.',
                };
            }
            return undefined;
        case 'second-approver':
            if (decider.isBreakGlass) {
                return {
                    status: 403,
                    error: 'break-glass-cannot-approve',
                    detail: 'A break-glass account cannot be the second approver: one person can hold both break-glass accounts, which would make four eyes two. The request waits for Identity Center.',
                };
            }
            return undefined;
        default:
            return assertNever(run.approval.requirement, 'approval requirement');
    }
};
/** The most runs one bulk approval may name. A bound, not a quota: one page of the queue. */
export const MAX_BULK_APPROVALS = 50;
/** Reads a bulk approval's run ids, or says what is wrong with them. */
export const readBulkApproval = (body) => {
    const ids = typeof body === 'object' && body !== null && !Array.isArray(body)
        ? body.runIds
        : undefined;
    if (!Array.isArray(ids) || ids.length === 0)
        return 'runIds must be a non-empty list of run ids';
    if (ids.length > MAX_BULK_APPROVALS) {
        return `at most ${String(MAX_BULK_APPROVALS)} runs may be approved at once`;
    }
    if (!ids.every((id) => typeof id === 'string' && id !== '')) {
        return 'every run id must be a non-empty string';
    }
    return [...new Set(ids)];
};
/** A rejection's reason, trimmed and bounded, or undefined when none was given. */
export const readRejectionReason = (body) => {
    const reason = typeof body === 'object' && body !== null ? body.reason : undefined;
    if (typeof reason !== 'string' || reason.trim() === '')
        return undefined;
    return reason.trim().slice(0, 500);
};
//# sourceMappingURL=approval.js.map